import { BaseClient } from '../base-client';
import type {
  ApprovalInstance,
  ApprovalInstanceStep,
  ApproverRow,
  BatchCheckInput,
  CancelInput,
  RejectInput,
} from './types';

type InstanceWithRelated = {
  data: ApprovalInstance | null;
  related?: { steps: ApprovalInstanceStep[]; approvers: ApproverRow[] };
};

type One = { data: ApprovalInstance };

type BatchCheckResponse = {
  data: Record<string, { canCurrentUserApprove: boolean }>;
};

export class ApprovalInstancesClient extends BaseClient {
  /** Returns null in `data` when no instance exists for the object. */
  getForObject(objectType: string, objectId: string): Promise<InstanceWithRelated> {
    return this.request(
      `/api/approval-instances/${encodeURIComponent(objectType)}/${encodeURIComponent(objectId)}`,
    );
  }

  batchCheck(input: BatchCheckInput): Promise<BatchCheckResponse> {
    return this.request('/api/approval-instances/batch-check', { method: 'POST', body: input });
  }

  approve(instanceId: string): Promise<One> {
    return this.request(`/api/approval-instances/${encodeURIComponent(instanceId)}/approve`, {
      method: 'POST',
    });
  }

  reject(instanceId: string, input: RejectInput): Promise<One> {
    return this.request(`/api/approval-instances/${encodeURIComponent(instanceId)}/reject`, {
      method: 'POST',
      body: input,
    });
  }

  cancel(instanceId: string, input: CancelInput): Promise<One> {
    return this.request(`/api/approval-instances/${encodeURIComponent(instanceId)}/cancel`, {
      method: 'POST',
      body: input,
    });
  }

  // ── Demo entity (Phase 1 scaffolding) ─────────────────────────────────

  /** Demo submit. Requires an Idempotency-Key header. */
  demoSubmit(
    body: { objectId: string; context: Record<string, unknown> },
    idempotencyKey: string,
  ): Promise<{ data: { accepted: true; objectId: string; requestId: string } }> {
    return this.request('/api/approval-demo/submit', {
      method: 'POST',
      body,
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  }

  demoCancel(body: {
    objectId: string;
    reason?: string | null;
  }): Promise<{ data: ApprovalInstance | null }> {
    return this.request('/api/approval-demo/cancel', { method: 'POST', body });
  }
}
